import { FC } from 'react';
import { FileUpload, Description, Close } from '@mui/icons-material';
import { Button as SMButton } from '../../../components';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { generateContractDocuments } from '../../../fetch';
import { DocumentContractingType } from '../../../models';

interface IGenerateContractModal {
  open: boolean;
  handleClose: () => void;
  refetchContractDocumentResults: () => void;
  setSaving: (isSaving: boolean) => void;
  specBuildId: string | number;
  setContractingType: (string) => void;
}

export const GenerateContractModal: FC<IGenerateContractModal> = ({
  open,
  handleClose,
  refetchContractDocumentResults,
  setSaving,
  specBuildId,
  setContractingType
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Modal open={open} onClose={handleClose}>
      <Box className={classes.modal}>
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <Close />
        </IconButton>
        <Typography id="contract-modal-title" variant="h6" component="h2" className={classes.title}>
          How would you like to generate the contract?
        </Typography>
        <Box className={classes.hr} />
        <Box className={classes.container}>
          <Box className={classes.digitalItem}>
            <Typography variant="h6">Contract Digitally</Typography>
            <Typography variant="body2">
              Create digital markup and send the contract over email for the Purchasers' signature.
            </Typography>
            <SMButton
              id="selections-details-link"
              color="primary"
              className={classes.actionButtons}
              variant="contained"
              startIcon={<Description />}
              onClick={async () => {
                try {
                  setSaving(true);
                  handleClose();
                  await generateContractDocuments(specBuildId, DocumentContractingType.Digital);
                  refetchContractDocumentResults();
                  setContractingType(DocumentContractingType.Digital);
                } catch (error) {
                  enqueueSnackbar(
                    error?.Detail ?? `Error generating contract documents, please try again.`,
                    {
                      variant: 'error'
                    }
                  );
                } finally {
                  setSaving(false);
                }
              }}
            >
              Create Packet
            </SMButton>
          </Box>
          <Box className={classes.printItem}>
            <Typography variant="h6">Printed Contract</Typography>
            <Typography variant="body2">
              Generate the Contract PDF for printing, and upload the final scanned packet.
            </Typography>
            <SMButton
              id="selections-details-link"
              color="primary"
              className={classes.actionButtons}
              variant="contained"
              startIcon={<FileUpload />}
              onClick={async () => {
                try {
                  setSaving(true);
                  handleClose();
                  await generateContractDocuments(specBuildId, DocumentContractingType.Scan);
                  refetchContractDocumentResults();
                  setContractingType(DocumentContractingType.Scan);
                } catch (error) {
                  enqueueSnackbar(
                    error?.Detail ?? `Error generating contract documents, please try again.`,
                    {
                      variant: 'error'
                    }
                  );
                } finally {
                  setSaving(false);
                }
              }}
            >
              Generate File
            </SMButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: 300,
    maxWidth: 525,
    backgroundColor: 'white',
    boxShadow:
      '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingBottom: '16px',
    paddingTop: '8px',
    boxSizing: 'border-box'
  },
  title: {
    paddingTop: '.5rem'
  },
  button: {
    marginTop: '16px'
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center'
    }
  },
  digitalItem: {
    backgroundColor: '#ebebeb',
    textAlign: 'center',
    marginRight: '8px',
    paddingTop: '16px',
    paddingBottom: '16px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: '16px'
    }
  },
  printItem: {
    backgroundColor: '#ebebeb',
    textAlign: 'center',
    marginLeft: '8px',
    paddingTop: '16px',
    paddingBottom: '16px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: '16px'
    }
  },
  icon: {
    marginRight: '8px'
  },
  actionButtons: {
    marginTop: '24px'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(0.25),
    top: theme.spacing(0.25),
    color: '#444',
    zIndex: 2
  },
  hr: {
    borderBottom: '1px solid #ebebeb',
    marginBottom: theme.spacing(2.5),
    marginTop: theme.spacing(0.25)
  }
}));
